import { useTranslationTyped } from 'i18n/i18n'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { Controller, useFieldArray } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import type { AssignmentFormValues } from '../schemas'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { BreakoutTextArea } from 'components/design-system/BreakoutTextArea'
import { DragVerticalFill } from 'components/icons/DragVerticalFill'
import classNames from 'classnames'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { PlusIcon } from 'components/icons/Plus'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
  type ClientRect,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS, type Transform } from '@dnd-kit/utilities'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { FormError } from 'components/design-system/form/FormError'
import { defaultPeerReviewCriteria } from 'pages/instructor/slide_deck/AssignmentDialog/helpers'
import { ConfirmationDialog } from 'components/dialogs/ConfirmationDialog'
import { useState, useRef, useCallback, useId } from 'react'
import { PeerReviewMeaningOfScores } from 'components/PeerReview/PeerReviewMeaningOfScores'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { isPeerReviewAllowed } from '@breakoutlearning/firebase-repository/sources-of-truth/isPeerReviewAllowed'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'

export const MAX_CRITERIA = 4
export const MAX_CRITERIA_NAME_LENGTH = 30
export const MAX_CRITERIA_DESCRIPTION_LENGTH = 180

export const PeerReviewInputs = ({
  form,
  readOnly = false,
}: {
  form: UseFormReturn<AssignmentFormValues>
  readOnly?: boolean
}) => {
  const { tt } = useTranslationTyped()
  const labelId = useId()
  const { control, watch, getFieldState, trigger } = form
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'peerReviewCriteria',
  })

  const onRemove = useCallback(
    (index: number) => {
      remove(index)
      if (fields.length === 1) {
        append({ prQuestion: '', prDescription: '' })
      }
    },
    [append, fields.length, remove]
  )

  const peerReviewsEnabled = watch('peerReviewsEnabled')
  const groupingSize = watch('groupingSize')
  const isPeerReviewDisabled = !isPeerReviewAllowed(groupingSize)

  const sensors = useSensors(useSensor(PointerSensor))

  const restrictToScrollContainer: Modifier = useCallback(
    ({ draggingNodeRect, transform }) => {
      const scrollContainer = scrollContainerRef.current
      const scrollContainerRect = scrollContainer?.getBoundingClientRect()

      if (!scrollContainerRect || !draggingNodeRect) {
        return transform
      }

      return restrictToBoundingRect(
        transform,
        draggingNodeRect,
        scrollContainerRect
      )
    },
    []
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((field) => field.id === active.id)
    const newIndex = fields.findIndex((field) => field.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex)
    }
  }

  const peerReviewCriteriaField = getFieldState('peerReviewCriteria')

  return (
    <div className="flex h-full w-full grow flex-col gap-2.5">
      <p
        id={labelId}
        className="text-center text-body-large text-on-surface-var"
      >
        {tt.instructor_assignment.configure_assignment_dialog.peer_reviews_description()}
      </p>

      {readOnly && (
        <div
          data-testid="peer-review-read-only-banner"
          aria-label={tt.common.alert()}
          role="alert"
          className="mb-1 flex flex-row items-center justify-center gap-2 rounded-xl border border-outline-light px-3 py-2"
        >
          <TriangleAlertIcon size={24} className="stroke-core-error" />
          <div className="text-body-medium">
            {tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_sessions_started()}
          </div>
        </div>
      )}

      <div className="flex w-[500px] flex-col gap-2 self-center">
        <BreakoutTooltip
          enabled={isPeerReviewDisabled}
          content={tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one()}
        >
          <div>
            <Controller
              control={control}
              name="peerReviewsEnabled"
              render={({ field }) => (
                <BreakoutRadioGroup
                  aria-labelledby={labelId}
                  value={field.value ? 'yes' : 'no'}
                  onValueChange={(val) => field.onChange(val === 'yes')}
                  disabled={readOnly || isPeerReviewDisabled}
                  className="flex w-full gap-1"
                >
                  <BreakoutRadioButton
                    variant="compact"
                    label={tt.common.no()}
                    value="no"
                    disabled={readOnly || isPeerReviewDisabled}
                    data-testid="peer-reviews-no"
                  />
                  <BreakoutRadioButton
                    variant="compact"
                    label={tt.common.yes()}
                    value="yes"
                    disabled={readOnly || isPeerReviewDisabled}
                    data-testid="peer-reviews-yes"
                  />
                </BreakoutRadioGroup>
              )}
            />
          </div>
        </BreakoutTooltip>
      </div>

      <div className="my-0.5 flex w-full flex-col gap-2">
        <div className="h-[1px] w-full bg-outline-light" />
      </div>

      {peerReviewsEnabled && (
        <div className="flex w-full flex-1 gap-10 overflow-y-auto">
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center justify-start">
              <h3 className="text-title-medium">
                {tt.instructor_assignment.configure_assignment_dialog.criteria()}
              </h3>
            </div>

            <div className="flex gap-4 text-body-medium text-on-surface-var">
              <p className="w-[250px]">
                {tt.instructor_assignment.configure_assignment_dialog.criteria_name()}
                <span className="text-core-error">*</span>
              </p>
              <p className="flex-1">
                {tt.instructor_assignment.configure_assignment_dialog.criteria_description()}
                <span className="text-core-error">*</span>
              </p>
            </div>

            <div
              className="my-0.5 gap-1 overflow-y-auto overflow-x-hidden"
              ref={scrollContainerRef}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToScrollContainer]}
              >
                <SortableContext
                  items={fields.map((field) => field.id)}
                  strategy={verticalListSortingStrategy}
                  disabled={readOnly}
                >
                  {fields.map((field, index) => (
                    <SortableCriteriaItem
                      key={field.id}
                      field={field}
                      index={index}
                      control={control}
                      readOnly={readOnly}
                      onRemove={onRemove}
                      tt={tt}
                      getFieldState={getFieldState}
                      trigger={trigger}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <div className="flex justify-between">
                <BreakoutTooltip
                  enabled={fields.length >= MAX_CRITERIA}
                  content={`Maximum of ${MAX_CRITERIA} criteria reached`}
                >
                  <div>
                    <BreakoutButton
                      type="button"
                      kind="secondary"
                      size="small"
                      icon={<PlusIcon size={15} />}
                      onClick={() => {
                        if (fields.length < MAX_CRITERIA) {
                          append({ prQuestion: '', prDescription: '' })
                        }
                      }}
                      disabled={readOnly || fields.length >= MAX_CRITERIA}
                      className={classNames({
                        'opacity-30': fields.length >= MAX_CRITERIA,
                      })}
                      data-testid="add-criteria-button"
                    >
                      {tt.instructor_assignment.configure_assignment_dialog.add_criteria()}
                    </BreakoutButton>
                  </div>
                </BreakoutTooltip>
                <BreakoutButton
                  type="button"
                  kind="secondary"
                  size="small"
                  onClick={() => {
                    setResetConfirmationOpen(true)
                  }}
                  disabled={readOnly}
                  data-testid="reset-to-defaults-button"
                >
                  {tt.instructor_assignment.configure_assignment_dialog.reset_to_defaults()}
                </BreakoutButton>
              </div>
            </div>
          </div>

          <div className="flex h-full flex-col justify-between gap-1">
            <div className="mb-2 mr-4 flex h-full flex-col gap-5">
              <PeerReviewMeaningOfScores />

              <Controller
                control={control}
                name="peerReviewsHiddenFromReviewees"
                render={({ field: hiddenField }) => (
                  <Controller
                    control={control}
                    name="peerReviewExcludesSelfReview"
                    render={({ field: selfReviewField }) => (
                      <BooleanSwitches
                        peerReviewExcludesSelfReview={
                          selfReviewField.value ?? false
                        }
                        peerReviewsHiddenFromReviewees={
                          hiddenField.value ?? false
                        }
                        onSelfReviewChange={selfReviewField.onChange}
                        onHiddenChange={hiddenField.onChange}
                        disabled={readOnly}
                      />
                    )}
                  />
                )}
              />
            </div>
            <FormError
              errorClass="mt-2"
              error={peerReviewCriteriaField.error}
            />
          </div>
        </div>
      )}

      {resetConfirmationOpen && (
        <ConfirmationDialog
          inline
          open={resetConfirmationOpen}
          title={tt.instructor_assignment.configure_assignment_dialog.reset_to_defaults()}
          subtitle={tt.instructor_assignment.configure_assignment_dialog.reset_to_defaults_confirm()}
          onConfirm={async () => {
            form.setValue('peerReviewCriteria', defaultPeerReviewCriteria(tt))
          }}
          dismiss={() => setResetConfirmationOpen(false)}
        />
      )}
    </div>
  )
}

const SortableCriteriaItem = ({
  field,
  index,
  control,
  readOnly,
  onRemove,
  tt,
  getFieldState,
  trigger,
}: {
  field: { id: string }
  index: number
  control: UseFormReturn<AssignmentFormValues>['control']
  readOnly: boolean
  onRemove: (index: number) => void
  tt: ReturnType<typeof useTranslationTyped>['tt']
  getFieldState: UseFormReturn<AssignmentFormValues>['getFieldState']
  trigger: UseFormReturn<AssignmentFormValues>['trigger']
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames('flex w-full gap-2 p-0.5', {
        'opacity-50': isDragging,
      })}
    >
      <div className="flex min-w-[250px] flex-col">
        <Controller
          control={control}
          name={`peerReviewCriteria.${index}.prQuestion`}
          render={({ field: nameField }) => (
            <BreakoutTextInput
              {...nameField}
              onChange={(e) => {
                nameField.onChange(e)
                trigger(`peerReviewCriteria.${index}.prQuestion`)
                trigger('peerReviewCriteria')
              }}
              kind="secondary"
              variant="compact"
              disabled={readOnly}
              maxLength={MAX_CRITERIA_NAME_LENGTH}
              showCharacterCount
              data-testid={`peer-review-criteria-name-${index}`}
              renderUnderneath={
                <div>
                  <FormError
                    errorClass="!mt-0"
                    error={
                      getFieldState(`peerReviewCriteria.${index}.prQuestion`)
                        .error
                    }
                  />
                  <button
                    type="button"
                    className={classNames(
                      'text-left text-on-surface-var underline',
                      {
                        'cursor-pointer hover:text-core-error': !readOnly,
                        'cursor-not-allowed opacity-50': readOnly,
                      }
                    )}
                    onClick={() => !readOnly && onRemove(index)}
                    disabled={readOnly}
                    data-testid={`remove-criteria-${index}`}
                  >
                    {tt.instructor_assignment.configure_assignment_dialog.remove()}
                  </button>
                </div>
              }
            />
          )}
        />
      </div>

      <div className="flex min-w-[350px] flex-1 flex-col gap-1">
        <Controller
          control={control}
          name={`peerReviewCriteria.${index}.prDescription`}
          render={({ field: descField }) => (
            <BreakoutTextArea
              {...descField}
              onChange={(e) => {
                descField.onChange(e)
                trigger(`peerReviewCriteria.${index}.prDescription`)
                trigger('peerReviewCriteria')
              }}
              kind="secondary"
              disabled={readOnly}
              maxLength={MAX_CRITERIA_DESCRIPTION_LENGTH}
              autoGrow
              inputClassName="!min-h-8 !rounded-lg !py-2 !overflow-y-hidden resize-none"
              data-testid={`peer-review-criteria-description-${index}`}
              renderUnderneath={
                <FormError
                  errorClass="!mt-0"
                  error={
                    getFieldState(`peerReviewCriteria.${index}.prDescription`)
                      .error
                  }
                />
              }
            />
          )}
        />
      </div>

      <div className="flex h-full items-start">
        <div
          {...attributes}
          {...listeners}
          className={classNames(
            'flex h-[calc(100%-16px)] items-center justify-center rounded-lg bg-surface-dim px-1 py-2',
            {
              'cursor-grab': !readOnly && !isDragging,
              'cursor-grabbing': !readOnly && isDragging,
              'cursor-not-allowed opacity-50': readOnly,
            }
          )}
        >
          <DragVerticalFill size={15} color="#6f6f6f" />
        </div>
      </div>
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function restrictToBoundingRect(
  transform: Transform,
  rect: ClientRect,
  boundingRect: ClientRect
): Transform {
  const value = {
    ...transform,
  }

  if (rect.top + transform.y <= boundingRect.top) {
    value.y = boundingRect.top - rect.top
  } else if (
    rect.bottom + transform.y >=
    boundingRect.top + boundingRect.height
  ) {
    value.y = boundingRect.top + boundingRect.height - rect.bottom
  }

  if (rect.left + transform.x <= boundingRect.left) {
    value.x = boundingRect.left - rect.left
  } else if (
    rect.right + transform.x >=
    boundingRect.left + boundingRect.width
  ) {
    value.x = boundingRect.left + boundingRect.width - rect.right
  }

  return value
}

export const BooleanSwitches = ({
  peerReviewExcludesSelfReview,
  peerReviewsHiddenFromReviewees,
  onSelfReviewChange,
  onHiddenChange,
  disabled = false,
}: {
  peerReviewExcludesSelfReview: boolean
  peerReviewsHiddenFromReviewees: boolean
  onSelfReviewChange: (value: boolean) => void
  onHiddenChange: (value: boolean) => void
  disabled?: boolean
}) => {
  const { tt } = useTranslationTyped()
  return (
    <>
      <div className="flex flex-col gap-2">
        <BreakoutSwitch
          accent
          label={
            <div className="flex w-full text-label-medium text-black">
              {tt.instructor_assignment.configure_assignment_dialog.display_peer_ratings()}
            </div>
          }
          labelPosition="left"
          labelClassName="!pr-3 !pl-0 mr-auto"
          checked={!peerReviewsHiddenFromReviewees}
          onChange={(e) => onHiddenChange(!e.target.checked)}
          disabled={disabled}
          className="!ml-0 w-full self-center text-label-large"
          data-testid="peer-reviews-displayed-to-peers-switch"
        />
        <div className="text-body-medium text-on-surface-var">
          {tt.instructor_assignment.configure_assignment_dialog.display_peer_ratings_description()}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <BreakoutSwitch
          accent
          label={
            <div className="flex w-full text-label-medium text-black">
              {tt.instructor_assignment.configure_assignment_dialog.participants_should_self_review()}
            </div>
          }
          labelPosition="left"
          labelClassName="!pr-3 !pl-0 mr-auto"
          checked={!peerReviewExcludesSelfReview}
          onChange={(e) => onSelfReviewChange(!e.target.checked)}
          disabled={disabled}
          className="!ml-0 w-full self-center text-label-large"
          data-testid="peer-review-includes-self-review-switch"
        />
        <div className="text-body-medium text-on-surface-var">
          {tt.instructor_assignment.configure_assignment_dialog.participants_should_self_review_description()}
        </div>
      </div>
    </>
  )
}
